import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Share2, Newspaper, Mail, X, CalendarClock, ArrowRight, Send, Repeat, CalendarX, Megaphone, CalendarPlus } from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

const TYPE_CONFIG = {
  social: { color: '#48B64C', label: 'Social', icon: Share2 },
  newsletter: { color: '#C7A67A', label: 'Newsletter', icon: Newspaper },
  email: { color: '#5B8DEF', label: 'Email', icon: Mail },
  weekly_fitter: { color: '#E0A82E', label: 'Weekly Fitter', icon: CalendarClock },
  email_campaign: { color: '#9B6DD6', label: 'Email Campaign', icon: Send },
  drip: { color: '#3FB8AF', label: 'Drip', icon: Repeat },
  recruit_drip: { color: '#E0699A', label: 'Recruit Drip', icon: Repeat },
  // A cadence commitment with nothing in it. Rendered as an OUTLINE chip so an empty
  // stretch reads as a missing send rather than as blank space — the Aug 10 2026 Weekly
  // Fitter slot sat here as literally nothing for eight days before anyone noticed.
  cadence_ghost: { color: '#8A94A6', label: 'Unfilled slot', icon: CalendarX, ghost: true },
  // A Meta AD (Randall, 18 Sep 2026): runs over a date range, so it is drawn as a BAR across
  // every day it runs, in a colour no post uses - an ad is paid reach, not a post.
  social_ad: { color: '#E8743B', label: 'Meta ad', icon: Megaphone, range: true },
  // A slot vacated on purpose, waiting for Randall to refill it. Calm grey and dotted: it is a
  // decision already made, never an alarm (the recruiting Thursdays moved to Meta ads).
  open_slot: { color: '#8A94A6', label: 'Open slot', icon: CalendarPlus, open: true },
};

// Does this event sit on this day? A range event (a Meta ad) sits on every day from its start
// to its end; everything else on the day its date names.
// A local date as YYYY-MM-DD — the same prefix onDay matches on.
export function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The window to ask /calendar/events for: the shown month, plus this week (the week view
// always shows the current week, whichever month is open). `to` is exclusive.
export function fetchWindow(year, month, today = new Date()) {
  const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
  const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7);
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 1);
  return {
    from: ymd(monthStart < weekStart ? monthStart : weekStart),
    to: ymd(monthEnd > weekEnd ? monthEnd : weekEnd),
  };
}

export function onDay(ev, dateStr) {
  if (ev.range_start && ev.range_end) return ev.range_start <= dateStr && dateStr <= ev.range_end;
  return Boolean(ev.date && ev.date.startsWith(dateStr));
}

// A range bar carries its label where a reader meets it: its first day, and each week's start.
export function isRangeHead(ev, dateStr, weekStart) {
  return !ev.range_start || ev.range_start === dateStr || weekStart;
}

// Ghost chips are drawn as a dashed outline with no fill, so they read as an absence at a
// glance and can never be mistaken for something that is actually scheduled. An urgent one
// (inside the commitment's lead_days) borrows the amber the dashboard uses for the same idea.
const GHOST_URGENT = '#E0A82E';
// A held slot is a decision, not a problem. It gets the calmest colour on the calendar so
// it can never read as an alarm — the whole reason holds exist is that a red chip for a
// considered choice teaches you to stop looking at red chips.
const GHOST_HELD = '#6B7480';

function chipColor(ev) {
  if (ev.type === 'cadence_ghost') {
    if (ev.held) return GHOST_HELD;
    if (ev.urgent) return GHOST_URGENT;
  }
  return TYPE_CONFIG[ev.type]?.color || '#8A94A6';
}

// The hover text IS the surface for a hold: the reason, and when it stops being good
// enough. An unfilled slot gets the prompt instead, because there is nothing to explain.
function chipTitle(ev) {
  if (!ev.ghost) return undefined;
  if (ev.held) {
    return `Held: ${ev.hold_reason || 'no reason recorded'}`
      + (ev.hold_expires_at ? ` · hold lapses ${ev.hold_expires_at}` : '');
  }
  return `${ev.title} — click to fill this slot`;
}

function chipStyle(ev, { pad = false } = {}) {
  const c = chipColor(ev);
  if (TYPE_CONFIG[ev.type]?.range) {
    // A bar: solid once it is at Meta, lighter while it is still a draft.
    return { backgroundColor: ev.planned ? `${c}30` : `${c}60`, color: c,
             borderLeft: `2px solid ${c}`, borderRadius: 0 };
  }
  if (TYPE_CONFIG[ev.type]?.open) {
    return { backgroundColor: 'transparent', color: c, border: `1px dotted ${c}` };
  }
  if (TYPE_CONFIG[ev.type]?.ghost) {
    return { backgroundColor: 'transparent', color: c, border: `1px dashed ${c}` };
  }
  // A PLANNED draft: dated, not scheduled. Dotted outline in its own type's colour, so it
  // reads as a plan at a glance - never as the solid chip of a send that is actually queued,
  // and never as the dashed grey of an unfilled slot. Nothing posts without an approval
  // and a deliberate schedule step; the chip must not suggest otherwise.
  if (ev.planned) {
    return { backgroundColor: `${c}08`, color: c, border: `1px dotted ${c}` };
  }
  return pad
    ? { backgroundColor: `${c}15`, borderLeft: `2px solid ${c}` }
    : { backgroundColor: `${c}20`, color: c, borderLeft: `2px solid ${c}` };
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function ContentCalendar({ onNavigate }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('month');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // One month at a time; changing month fetches that month. A response for a month the
  // reader has already moved past is dropped.
  useEffect(() => {
    let stale = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/calendar/events`, { params: fetchWindow(year, month) });
        if (!stale) setEvents(res.data.events);
      } catch (e) { console.error(e); }
      if (!stale) setLoading(false);
    })();
    return () => { stale = true; };
  }, [year, month]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [firstDay, daysInMonth]);

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => onDay(e, dateStr));
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthName = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const today = new Date();
  const currentWeekStart = new Date(today);
  currentWeekStart.setDate(today.getDate() - today.getDay());

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(currentWeekStart);
    d.setDate(currentWeekStart.getDate() + i);
    return d;
  });

  return (
    <div className="space-y-6">
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setSelectedEvent(null)}>
          <div className="bg-hg-surface border border-hg-border rounded-sm max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={selectedEvent.ghost
                  ? { border: `1px dashed ${chipColor(selectedEvent)}` }
                  : { backgroundColor: chipColor(selectedEvent) }} />
                <span className="text-xs font-sans uppercase tracking-wider text-hg-text-dim">{TYPE_CONFIG[selectedEvent.type]?.label}</span>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="text-hg-text-dim hover:text-hg-text"><X size={16} /></button>
            </div>
            <h3 className="font-display text-lg text-hg-text tracking-wider mb-2">{selectedEvent.title}</h3>
            <p className="text-xs text-hg-text-dim font-sans mb-3">
              {selectedEvent.date ? new Date(selectedEvent.date).toLocaleString() : 'No date'}
            </p>
            <p className="text-sm text-hg-text/90 font-sans">{selectedEvent.preview}</p>
            {selectedEvent.type === 'social_ad' && (
              <div data-testid="cal-ad-detail" className="mt-3 border border-hg-border rounded-sm p-3 text-[11px] font-sans text-hg-text-dim">
                Runs <strong className="text-hg-text">{selectedEvent.range_start}</strong> to{' '}
                <strong className="text-hg-text">{selectedEvent.range_end}</strong>
                {selectedEvent.budget ? ` · ${selectedEvent.budget}` : ''} · {selectedEvent.ad_status}.
                {' '}A Meta ad is pushed PAUSED after Randall approves it; he activates it in Ads Manager.
              </div>
            )}
            {selectedEvent.type === 'open_slot' && (
              <p data-testid="cal-open-detail" className="text-[11px] text-hg-text-dim font-sans mt-2">
                Open on purpose — nothing is missing here. It stays open until Randall refills it.
              </p>
            )}
            {selectedEvent.planned && selectedEvent.type !== 'open_slot' && selectedEvent.type !== 'social_ad' && (
              <p data-testid="cal-planned-detail" className="text-[11px] text-hg-text-dim font-sans mt-2">
                Planned, not scheduled{selectedEvent.campaign_id ? ` · ${selectedEvent.campaign_id}` : ''}. It goes out only once it is approved and scheduled, one post at a time.
              </p>
            )}
            {selectedEvent.held && (
              <div className="mt-3 border border-hg-border rounded-sm p-3" data-testid="cal-hold-detail">
                <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim mb-1">
                  Held on purpose
                </div>
                <div className="text-sm font-sans text-hg-text">
                  {selectedEvent.hold_reason || 'No reason recorded'}
                </div>
                {selectedEvent.hold_expires_at && (
                  <div className="text-[11px] font-sans text-hg-text-dim mt-1">
                    Hold lapses {selectedEvent.hold_expires_at}
                    {typeof selectedEvent.hold_lapses_in_days === 'number'
                      && ` — ${selectedEvent.hold_lapses_in_days} day${selectedEvent.hold_lapses_in_days === 1 ? '' : 's'} to decide`}
                    . After that this slot reports as a gap again.
                  </div>
                )}
              </div>
            )}
            {selectedEvent.target && (
              <p className="text-xs text-hg-text-dim font-sans mt-2">
                Sending to <strong className="text-hg-text">{selectedEvent.target}</strong>
                {selectedEvent.test && <span className="text-amber-500"> (test dry-run)</span>}
              </p>
            )}
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-sm font-sans ${
                selectedEvent.status === 'scheduled' || selectedEvent.status === 'pushed'
                  ? 'bg-hg-green/10 text-hg-green' : 'bg-hg-border text-hg-text-dim'
              }`}>{selectedEvent.status}</span>
              {selectedEvent.type === 'weekly_fitter' && !selectedEvent.planned && onNavigate && (
                <button
                  onClick={() => { setSelectedEvent(null); onNavigate('email', { tab: 'weekly' }); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover"
                >
                  Open in composer <ArrowRight size={13} />
                </button>
              )}
              {/* A planned draft links to where it is reviewed. It is not scheduled and the
                  button does not schedule it - approving and scheduling stay in the Drafts
                  tab or the composer, one deliberate step at a time. */}
              {(selectedEvent.planned || selectedEvent.type === 'social_ad') && onNavigate && (
                <button
                  data-testid="cal-planned-open"
                  onClick={() => {
                    setSelectedEvent(null);
                    // Same deep-link shape as the dashboard row: Social Studio reads
                    // `?tab=drafts` at mount, so the URL is set before the module switches.
                    try {
                      const p = new URLSearchParams();
                      p.set('module', selectedEvent.module || 'email');
                      if (selectedEvent.tab) p.set('tab', selectedEvent.tab);
                      window.history.replaceState(null, '', `?${p.toString()}`);
                    } catch { /* URL update is best-effort */ }
                    onNavigate(selectedEvent.module || 'email',
                      selectedEvent.tab ? { tab: selectedEvent.tab } : undefined);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text-dim rounded-sm hover:text-hg-text hover:border-hg-text-dim"
                >
                  Open draft <ArrowRight size={13} />
                </button>
              )}
              {selectedEvent.type === 'email_campaign' && onNavigate && (
                <button
                  onClick={() => { setSelectedEvent(null); onNavigate('email', { tab: 'campaigns' }); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover"
                >
                  Open in Email Studio <ArrowRight size={13} />
                </button>
              )}
              {/* A ghost only ever LINKS. The watchdog never drafts or schedules —
                  filling the slot is Randall's act, in the composer. A HELD slot leads with
                  its reason instead of a prompt: it is empty on purpose, and the useful
                  information is why and until when, not "go fill this". */}
              {selectedEvent.ghost && onNavigate && (
                <button
                  data-testid={selectedEvent.held ? 'cal-hold-open-composer' : 'cal-ghost-open-composer'}
                  onClick={() => {
                    setSelectedEvent(null);
                    onNavigate(selectedEvent.module || 'email',
                      selectedEvent.tab ? { tab: selectedEvent.tab } : undefined);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans rounded-sm ${
                    selectedEvent.held
                      ? 'border border-hg-border text-hg-text-dim hover:text-hg-text hover:border-hg-text-dim'
                      : 'bg-hg-green text-hg-text hover:bg-hg-green-hover'
                  }`}
                >
                  {selectedEvent.held ? 'Fill it early' : 'Fill this slot'} <ArrowRight size={13} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">CONTENT CALENDAR</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">Unified view of all scheduled content across channels.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="cal-prev" onClick={prevMonth} className="p-1.5 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-green hover:border-hg-green transition-colors">
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <h2 className="font-display text-lg lg:text-xl text-hg-text min-w-[160px] lg:min-w-[200px] text-center tracking-wider">{monthName}</h2>
          <button data-testid="cal-next" onClick={nextMonth} className="p-1.5 border border-hg-border rounded-sm text-hg-text-dim hover:text-hg-green hover:border-hg-green transition-colors">
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="hidden sm:flex gap-3">
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <div key={key} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={cfg.ghost
                  ? { border: `1px dashed ${cfg.color}` } : { backgroundColor: cfg.color }} />
                <span className="text-xs font-sans text-hg-text-dim">{cfg.label}</span>
              </div>
            ))}
          </div>
          <div className="flex border border-hg-border rounded-sm overflow-hidden">
            <button
              data-testid="cal-view-month"
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-xs font-sans transition-colors ${viewMode === 'month' ? 'bg-hg-green text-hg-text' : 'text-hg-text-dim hover:text-hg-text'}`}
            >Month</button>
            <button
              data-testid="cal-view-week"
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-xs font-sans transition-colors ${viewMode === 'week' ? 'bg-hg-green text-hg-text' : 'text-hg-text-dim hover:text-hg-text'}`}
            >Week</button>
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-hg-text-dim text-sm">Loading calendar...</div>}

      {!loading && viewMode === 'month' && (
        <div className="bg-hg-surface border border-hg-border rounded-sm overflow-x-auto">
          <div className="min-w-[640px]">
          <div className="grid grid-cols-7 border-b border-hg-border">
            {weekDays.map(d => (
              <div key={d} className="px-2 py-2.5 text-center text-xs font-sans text-hg-text-dim uppercase tracking-wider">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {calendarDays.map((day, i) => {
              const dayEvents = getEventsForDay(day);
              const isToday = day && today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
              return (
                <div
                  key={i}
                  className={`min-h-[100px] border-b border-r border-hg-border p-1.5 ${
                    !day ? 'bg-hg-bg' : 'bg-hg-surface hover:bg-hg-surface-hover'
                  }`}
                >
                  {day && (
                    <>
                      <div className={`text-xs font-sans mb-1 ${
                        isToday ? 'w-6 h-6 rounded-full bg-hg-green text-hg-text flex items-center justify-center font-medium' : 'text-hg-text-dim px-1'
                      }`}>{day}</div>
                      {dayEvents.map((ev, j) => {
                        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const head = isRangeHead(ev, dateStr, i % 7 === 0);
                        return (
                        <button
                          key={j}
                          onClick={() => setSelectedEvent(ev)}
                          data-testid={ev.ghost ? `cal-${ev.held ? 'hold' : 'ghost'}-${ev.commitment_key}`
                            : (ev.type === 'social_ad' ? `cal-ad-${ev.draft_id}-${dateStr}` : undefined)}
                          title={ev.range_start ? `${ev.title} · ${ev.range_start} → ${ev.range_end}` : chipTitle(ev)}
                          className="w-full text-left px-1.5 py-0.5 mb-0.5 rounded-sm text-[10px] font-sans truncate transition-opacity hover:opacity-80"
                          style={chipStyle(ev)}
                        >
                          {ev.range_start ? (head ? ev.title : ' ')
                            : ev.ghost ? `${ev.held ? '❙ ' : '+ '}${ev.title}`
                            : (ev.planned ? `◌ ${ev.title}` : ev.title)}
                        </button>
                        );
                      })}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      )}

      {!loading && viewMode === 'week' && (
        <div className="bg-hg-surface border border-hg-border rounded-sm overflow-x-auto">
          <div className="min-w-[640px]">
          <div className="grid grid-cols-7 border-b border-hg-border">
            {weekDates.map((d, i) => (
              <div key={i} className="px-2 py-2.5 text-center border-r border-hg-border last:border-r-0">
                <div className="text-xs font-sans text-hg-text-dim uppercase">{weekDays[i]}</div>
                <div className={`text-sm font-sans mt-1 ${
                  d.toDateString() === today.toDateString() ? 'text-hg-green font-medium' : 'text-hg-text'
                }`}>{d.getDate()}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 min-h-[300px]">
            {weekDates.map((d, i) => {
              const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              const dayEvents = events.filter(e => onDay(e, dateStr));
              return (
                <div key={i} className="border-r border-hg-border last:border-r-0 p-2 space-y-1">
                  {dayEvents.map((ev, j) => (
                    <button
                      key={j}
                      onClick={() => setSelectedEvent(ev)}
                      data-testid={ev.ghost ? `cal-week-${ev.held ? 'hold' : 'ghost'}-${ev.commitment_key}` : undefined}
                      title={chipTitle(ev)}
                      className="w-full text-left p-2 rounded-sm text-xs font-sans transition-opacity hover:opacity-80"
                      style={chipStyle(ev, { pad: true })}
                    >
                      <div style={{ color: chipColor(ev) }} className="font-medium">{ev.held ? 'Held' : TYPE_CONFIG[ev.type]?.label}</div>
                      <div className={`truncate mt-0.5 ${ev.ghost ? 'text-hg-text-dim' : 'text-hg-text'}`}>{ev.title}</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      )}

      {!loading && events.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <div className="text-hg-text-dim text-sm font-sans">No scheduled content yet.</div>
          <p className="text-xs text-hg-text-dim font-sans">Create and schedule content in Social Studio, Newsletter Studio, or Email Studio to see it here.</p>
        </div>
      )}
    </div>
  );
}
